import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_VERSION_NUMBERS } from '../../graphql';

/**
 * One set of version numbers. History counts saves; a published revision counts
 * publishes, so once a draft is saved between publishes the same version would
 * have two numbers. The panels that name a version of an article or page ask
 * the server which of History's it is (tinycmsVersionNumbers). Other kinds have
 * no History and keep their own numbers.
 */

export interface SavedNumbers {
  byRevision: Record<string, number>;
  working: number | null;
}

/** History's number for a published revision. Null when it isn't known (yet). */
export const savedNumber = (
  numbers: SavedNumbers | null,
  revision: number | null | undefined
): number | null => (revision && numbers?.byRevision?.[revision]) || null;

interface VersionedState {
  live?: { version: number | null } | null;
  scheduled?: { version: number | null } | null;
  workingVersion?: number | null;
}

/** The state with History's numbers in it. Nothing is said by number until they've loaded. */
export function withSavedNumbers<S extends VersionedState>(
  state: S,
  numbers: SavedNumbers | null
): S {
  return {
    ...state,
    live: state.live
      ? { ...state.live, version: savedNumber(numbers, state.live.version) }
      : state.live,
    scheduled: state.scheduled
      ? { ...state.scheduled, version: savedNumber(numbers, state.scheduled.version) }
      : state.scheduled,
    workingVersion: numbers?.working ?? null,
  };
}

const HAS_HISTORY = ['article', 'page'];

/**
 * stamp: anything that changes when the item does (its hash, what's live). A
 * new one fetches again. A server without the field (a deploy in between)
 * leaves the numbers as they were: `active` is false, and `of` returns its input.
 */
export function useSavedNumbers(kind: string, id: string | null | undefined, stamp?: unknown) {
  const { accountId } = useAccount();
  const has = HAS_HISTORY.includes(kind) && Boolean(id) && Boolean(accountId);
  const { data, error, refetch } = useQuery<{ tinycmsVersionNumbers: SavedNumbers }>(
    GET_TINYCMS_VERSION_NUMBERS,
    {
      variables: { accountId, kind, id },
      skip: !has,
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    }
  );
  const numbers = data?.tinycmsVersionNumbers || null;
  const active = has && !(error && !numbers);

  const seen = useRef(stamp);
  useEffect(() => {
    if (seen.current === stamp) return;
    seen.current = stamp;
    if (has) void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp, has]);

  const of = useCallback(
    (revision: number | null | undefined) =>
      active ? savedNumber(numbers, revision) : (revision ?? null),
    [active, numbers]
  );

  /** A publish just happened: "v7", from fresh numbers, or "this version". */
  const nameOf = useCallback(
    async (revision: number | null | undefined) => {
      if (!active) return revision ? `v${revision}` : 'this version';
      const fresh = await refetch().catch(() => null);
      const n = savedNumber(fresh?.data?.tinycmsVersionNumbers || numbers, revision);
      return n ? `v${n}` : 'this version';
    },
    [active, numbers, refetch]
  );

  return { active, numbers, of, nameOf };
}
